"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.manageAthenaRecurringDateConfigOutputs = exports.constructAthenaResultSchema = exports.interpretValueType = exports.constructSqlQueryString = exports.evaluateFormulaBindings = exports.findVariablesReferenced = void 0;
const common_1 = require("@finley-technologies/common");
const finley_date_1 = require("@finley-technologies/finley-date");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const _isFormula = (input) => {
    return typeof input === 'string' && input.startsWith('=');
};
const findVariablesReferenced = (bindings) => {
    const variablesReferenced = new Set();
    for (const binding of bindings) {
        if (_isFormula(binding)) {
            const formula = binding.slice(1);
            const { variables } = (0, formula_engine_1.inspectDependencies)(formula);
            variables.forEach((variable) => variablesReferenced.add(variable));
        }
    }
    return Array.from(variablesReferenced);
};
exports.findVariablesReferenced = findVariablesReferenced;
const evaluateVariables = (params) => {
    const { variables } = params;
    const results = [];
    const errors = [];
    const sanitizedFormulas = variables
        .filter(({ value }) => _isFormula(value))
        .map(({ value }) => value.slice(1));
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, sanitizedFormulas, undefined);
    const variableNames = new Set();
    for (const { name, value, parseNumber } of variables) {
        let evaluatedValue;
        if (!_isFormula(value)) {
            // *** Manage non-formula value by just parsing it directly ***
            evaluatedValue = common_1.stringConversion.parseStringValueToSqlPrimitive(
            // We're actually parsing a string here to potentially another type.
            // "parseNumber" here represents the trigger whether to attempt parsing
            // the input value (which is a string)
            { value, parseString: parseNumber });
        }
        else {
            // *** Manage formula value ***
            const formula = value.slice(1);
            const formulaErrors = (0, formula_engine_1.getFormulaErrors)(formula, variableNames);
            if (formulaErrors.errorMessages.length > 0) {
                formulaErrors.errorMessages.forEach((err) => errors.push(err));
                continue;
            }
            const evaluatedFormula = formulaEngine.evaluate(formula);
            if (!finley_types_1.typeguards.isSqlPrimitive(evaluatedFormula)) {
                errors.push(`Evaluated non-primitive formula [value:${value}] [result:${evaluatedFormula}]`);
                continue;
            }
            evaluatedValue = evaluatedFormula;
        }
        formulaEngine.setVariables({ [name]: evaluatedValue }, { addToCurrentInput: true });
        variableNames.add(name);
        results.push({ name, value: evaluatedValue });
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    return { isValid: true, results };
};
const evaluateFormulaBindings = ({ variables, bindings, }) => {
    const variableNames = new Set(Object.keys(variables));
    const formulas = bindings.filter((binding) => _isFormula(binding));
    const formulaEngine = (0, formula_engine_1.createFormulaEngine)(finley_types_1.FormulaEngine.SupportedFormulaEngines.HyperFormula1, formulas.map((formula) => formula.slice(1)), undefined);
    formulaEngine.setVariables(variables);
    const evaluatedBindings = [];
    bindings.forEach((binding) => {
        if (!_isFormula(binding)) {
            evaluatedBindings.push(binding);
            return;
        }
        const formula = binding.slice(1);
        const errors = (0, formula_engine_1.getFormulaErrors)(formula, variableNames);
        (0, common_1.finleyAssert)(errors.errorMessages.length === 0, new common_1.error.FinleyError(`Errors detected when evaluating binding formulas.\nMessages: ${errors.errorMessages}\nMissing Functions: ${errors.missingFunctions}\nMissing Variables: ${errors.missingVariables}\n`, {
            metadata: {
                logMeta: {
                    errorMessages: JSON.stringify(errors.errorMessages),
                    missingFunctions: JSON.stringify(errors.missingFunctions),
                    missingVariables: JSON.stringify(errors.missingVariables),
                },
            },
        }));
        const evaluatedBinding = formulaEngine.evaluate(formula);
        (0, common_1.finleyAssert)(finley_types_1.typeguards.isSqlPrimitive(evaluatedBinding), `Expected evaluated binding formula to resolve to string|number|null|boolean, but got ${evaluatedBinding}`);
        evaluatedBindings.push(evaluatedBinding);
    });
    return evaluatedBindings;
};
exports.evaluateFormulaBindings = evaluateFormulaBindings;
const constructSqlQueryString = ({ query, bindings, variables, }) => {
    const variableResults = evaluateVariables({ variables });
    if (!variableResults.isValid) {
        return { isValid: false, errors: variableResults.errors };
    }
    const { keyMap, errors } = (0, formula_engine_1.keyByVariableName)(variableResults.results);
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    const evaluatedBindings = (0, exports.evaluateFormulaBindings)({
        variables: keyMap,
        bindings,
    });
    const queryString = common_1.datalake.athena.generateBindedSqlString({
        query,
        bindings: evaluatedBindings,
    });
    return { isValid: true, queryString };
};
exports.constructSqlQueryString = constructSqlQueryString;
// TODO: add unit tests on interpretation here
const interpretValueType = (val) => {
    if (typeof val === 'boolean') {
        return 'boolean';
    }
    if (typeof val === 'number') {
        return 'number';
    }
    if (typeof val === 'string') {
        if (['true', 'false'].includes(val.toLowerCase())) {
            return 'boolean';
        }
        // TODO: add "date" support
        // if (
        //   commonDate.isStringAbleToBeDateType(val) ||
        //   commonTypeguards.isFullDateStr(val) ||
        //   commonTypeguards.isFullDateTimeStr(val)
        // ) {
        //   return 'date';
        // }
        if (typeof common_1.stringConversion.currencyStrToNumber(val, finley_types_1.Bank.Currency.USD) === 'number') {
            return 'number';
        }
        if (common_1.utils.isNumericString(val)) {
            return 'number';
        }
        return 'string';
    }
    throw new common_1.error.FinleyError(`Detected unsupported [type:${typeof val}] while interpreting [val:${val}] for Athena schema`);
};
exports.interpretValueType = interpretValueType;
const constructAthenaResultSchema = (items, options = {}) => {
    const schema = [];
    const keyTypeMap = new Map();
    const errors = [];
    const targetItems = options.onlyEvaluateFirst ? items.slice(0, 1) : items;
    for (const item of targetItems) {
        for (const [key, val] of Object.entries(item)) {
            try {
                // Currently not going to try and interpret the value ("interpretValueType" function),
                // as we purposely want to take the face value "type" of the queried results.
                // If the typing here isn't what is expected, then this is likely highlighting the
                // Datalake schema is incorrect (e.g., incorrect sanitize process from BBPL).
                // If truly needed, can always reconfigure SQL query to apply casting as needed
                const type = typeof val;
                // Critical error if come across unsupported type so return early
                if (!finley_types_1.typeguards.datalake.isAthenaResultSchemaType(type)) {
                    errors.push(`Came across unsupported Athena Result Schema type [key:${key}] [val:${val}] [type:${type}]`);
                    return { isValid: false, errors };
                }
                const seenTypesSet = keyTypeMap.get(key) ?? new Set();
                seenTypesSet.add(type);
                keyTypeMap.set(key, seenTypesSet);
            }
            catch (error) {
                errors.push(`Came across unsupported Athena Result Schema type [key:${key}] [val:${val}] [type:${typeof val}]`);
                return { isValid: false, errors };
            }
        }
    }
    for (const [key, typeSet] of keyTypeMap.entries()) {
        const typeArray = Array.from(typeSet);
        if (typeArray.length > 1) {
            const seenTypes = Array.from(typeSet).join(', ');
            errors.push(`Came across multiple types for [key:${key}] [types:${seenTypes}]`);
        }
        else {
            const type = typeArray[0];
            schema.push({ key, type });
        }
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    return { isValid: true, schema };
};
exports.constructAthenaResultSchema = constructAthenaResultSchema;
/**
 * "allMaxCount" refers to the max "maxCount" attribute across all referenced
 * global recurring date configs. Recall that this figure is user authored and denotes
 * how many the user can expect as the max output, helping populate variables in case
 * the dynamic output doesn't generate a long enough list
 *
 * "maxAllAvailableCount" refers to the actual results of generating the lists and
 * denotes what every list has at least the number of dates for
 */
const manageAthenaRecurringDateConfigOutputs = (params) => {
    const { globalRecurringDateConfigFields, globalRecurringDateConfigs, supplementalVariables, athenaQueryName, } = params;
    let allMaxCount = 0;
    const dateConfigResults = [];
    const errors = [];
    let maxAllAvailableCount = Infinity;
    for (const dateField of globalRecurringDateConfigFields) {
        const targetDateConfig = globalRecurringDateConfigs.find(({ field }) => field === dateField);
        if (!targetDateConfig) {
            errors.push(`Missing global recurring date config [athenaQueryName:${athenaQueryName}] [field:${dateField}]`);
            continue;
        }
        const dateListResults = finley_date_1.recurringDate.generateDateListFromRecurringDateConfig({ recurringDateConfig: targetDateConfig, supplementalVariables });
        if (!dateListResults.isValid) {
            dateListResults.errors.forEach((err) => errors.push(err));
            continue;
        }
        const { dateList } = dateListResults;
        const { field, maxCount } = targetDateConfig;
        allMaxCount = Math.max(allMaxCount, maxCount);
        maxAllAvailableCount = Math.min(maxAllAvailableCount, dateList.length);
        dateConfigResults.push({ field, dateList });
    }
    if (errors.length > 0) {
        return { isValid: false, errors };
    }
    // If never got reduced, then default to 0
    if (maxAllAvailableCount === Infinity) {
        maxAllAvailableCount = 0;
    }
    return {
        isValid: true,
        allMaxCount,
        maxAllAvailableCount,
        dateConfigResults,
    };
};
exports.manageAthenaRecurringDateConfigOutputs = manageAthenaRecurringDateConfigOutputs;
//# sourceMappingURL=construct.js.map